import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Wand2, ImageIcon, Download, Trash2, Copy, RefreshCw, Sparkles, Check } from 'lucide-react';
import { AdminAiSettingsCard } from './admin/AdminAiSettingsCard';
import { AdminConseilsMediaCard } from './admin/AdminConseilsMediaCard';
import { AdminAiKeysCard } from './admin/AdminAiKeysCard';
import { AdminAiUsageCard } from './admin/AdminAiUsageCard';
import { AdminCityContentCard } from './admin/AdminCityContentCard';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const STYLE_LABELS = { photo: 'Photo réaliste lumineuse', illustration: 'Illustration flat pastel turquoise' };

const PROMPT_IDEAS = [
  { label: 'Ménage – cuisine', prompt: 'A sparkling clean modern kitchen with a microfiber cloth and eco-friendly cleaning products on the counter, morning light' },
  { label: 'Jardin – tonte', prompt: 'A freshly mowed green lawn with neat stripes, trimmed hedge and a small garden shed in a French village' },
  { label: 'Repassage', prompt: 'A neat stack of freshly ironed shirts and linens on an ironing board in a bright laundry corner' },
  { label: 'Bricolage', prompt: 'A wall shelf being installed with a level, drill and screws on a wooden workbench, warm light' },
  { label: 'Ville – Arras', prompt: 'A charming brick townhouse street in Arras, Flemish baroque facades, sunny afternoon, tidy front gardens' },
  { label: 'Réseaux – bienveillance', prompt: 'Hands passing a set of house keys with a small plant and a cup of tea on a wooden table, cozy welcoming home' },
];

const FADE_INITIAL = { opacity: 0 };
const FADE_ANIMATE = { opacity: 1 };

const copyUrl = async (url) => {
  try {
    await navigator.clipboard.writeText(`${window.location.origin}${url}`);
    toast.success('Lien copié');
  } catch (err) { logError('copie lien', err); toast.error('Copie impossible'); }
};

/** Generator form (prompt, ideas, style, label) + result preview. */
function ImageGeneratorCard({ adminKey, onGenerated }) {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('illustration');
  const [label, setLabel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [lastImage, setLastImage] = useState(null);

  const generate = async () => {
    if (prompt.trim().length < 5) { toast.error('Décrivez la scène (5 caractères minimum)'); return; }
    setGenerating(true); setLastImage(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/images/generate?key=${adminKey}`, { prompt: prompt.trim(), style, label: label.trim() });
      setLastImage(res.data);
      toast.success('Visuel généré');
      onGenerated(res.data);
    } catch (err) {
      logError('génération visuel', err);
      toast.error(err?.response?.data?.detail || 'La génération a échoué');
    } finally { setGenerating(false); }
  };

  const renderResult = () => {
    if (generating) return <Skeleton className="w-full h-full" />;
    if (lastImage) return <img src={lastImage.url} alt="Visuel généré" className="w-full h-full object-cover" />;
    return <div className="text-center text-muted-foreground p-4"><ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-40" /><p className="text-xs">Le visuel apparaîtra ici</p></div>;
  };

  return (
    <Card className="p-5 sm:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><Wand2 className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold">Générateur de visuels IA</h3>
          <p className="text-xs text-muted-foreground">Gemini Nano Banana — pour vos pages ville, réseaux sociaux, articles. Les visuels sont compressés automatiquement.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-3">
          <div>
            <label className="text-xs font-medium block mb-1">Décrivez la scène (français ou anglais)</label>
            <Textarea rows={3} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Ex : un salon lumineux et parfaitement rangé avec un bouquet de fleurs fraîches sur la table basse" data-testid="image-prompt-input" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PROMPT_IDEAS.map(i => (
              <button key={i.label} type="button" onClick={() => setPrompt(i.prompt)} className="text-[11px] px-2.5 py-1 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors" data-testid="image-prompt-idea">{i.label}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">Style</label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger data-testid="image-style-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="illustration">{STYLE_LABELS.illustration}</SelectItem>
                  <SelectItem value="photo">{STYLE_LABELS.photo}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Libellé (optionnel)</label>
              <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Ex : Facebook septembre" data-testid="image-label-input" />
            </div>
          </div>
          <Button onClick={generate} disabled={generating} data-testid="image-generate-btn">
            {generating ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Génération (20-40 s)…</> : <><Sparkles className="h-4 w-4 mr-2" /> Générer le visuel</>}
          </Button>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1">Résultat</label>
          <div className="aspect-video rounded-xl border border-border bg-[hsl(var(--secondary))]/60 overflow-hidden flex items-center justify-center" data-testid="image-result">
            {renderResult()}
          </div>
          {lastImage && (
            <div className="flex gap-2 mt-2">
              <a href={lastImage.url} download target="_blank" rel="noopener noreferrer" className="flex-1"><Button size="sm" variant="outline" className="w-full" data-testid="image-result-download"><Download className="h-3.5 w-3.5 mr-1" /> Télécharger</Button></a>
              <Button size="sm" variant="outline" onClick={() => copyUrl(lastImage.url)} data-testid="image-result-copy"><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function GalleryItem({ img, isNew, onRemove }) {
  const usageLabel = img.usage === 'ptitconseil' ? 'Ptitconseil' : 'Admin';
  const styleLabel = img.style === 'photo' ? 'Photo' : 'Illu.';
  return (
    <motion.div initial={FADE_INITIAL} animate={FADE_ANIMATE} className="group relative rounded-lg overflow-hidden border border-border bg-card" data-testid="gallery-image">
      <img src={img.url} alt={img.prompt} className="w-full aspect-video object-cover" loading="lazy" />
      <div className="p-2">
        <p className="text-[11px] text-muted-foreground line-clamp-2" title={img.prompt}>{img.ref && img.usage === 'admin' && <strong>{img.ref} — </strong>}{img.prompt}</p>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] text-muted-foreground">{usageLabel} · {styleLabel} · {Math.round((img.size || 0) / 1024)} Ko</span>
          <div className="flex gap-0.5">
            <a href={img.url} download target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-[hsl(var(--secondary))]" aria-label="Télécharger"><Download className="h-3.5 w-3.5" /></a>
            <button type="button" onClick={() => copyUrl(img.url)} className="p-1 rounded hover:bg-[hsl(var(--secondary))]" aria-label="Copier le lien" data-testid="gallery-copy"><Copy className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onRemove(img.id)} className="p-1 rounded hover:bg-destructive/10 text-destructive" aria-label="Supprimer" data-testid="gallery-delete"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      </div>
      {isNew && <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-[hsl(var(--primary))] text-white text-[10px] flex items-center gap-1"><Check className="h-3 w-3" /> Nouveau</span>}
    </motion.div>
  );
}

function ImageGalleryCard({ images, loading, newImageId, onRefresh, onRemove }) {
  const renderGrid = () => {
    if (loading) {
      return <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={`skeleton-${i}`} className="aspect-video rounded-lg" />)}</div>;
    }
    if (images.length === 0) {
      return <p className="text-sm text-muted-foreground text-center py-8" data-testid="images-empty">Aucun visuel généré pour le moment.</p>;
    }
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="images-gallery">
        {images.map(img => <GalleryItem key={img.id} img={img} isNew={newImageId === img.id} onRemove={onRemove} />)}
      </div>
    );
  };
  return (
    <Card className="p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold">Galerie ({images.length})</h3>
          <p className="text-xs text-muted-foreground">Tous les visuels générés. Copiez le lien pour l'utiliser ailleurs ou téléchargez-le.</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onRefresh} data-testid="images-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {renderGrid()}
    </Card>
  );
}

export function AdminImagesTab({ adminKey }) {
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [newImageId, setNewImageId] = useState(null);
  const [mediaRefreshToken, setMediaRefreshToken] = useState(0);
  const [keysRefreshToken, setKeysRefreshToken] = useState(0);

  const loadImages = useCallback(async () => {
    setLoadingImages(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/images?key=${adminKey}&limit=60`);
      setImages(res.data.items || []);
    } catch (err) {
      logError('galerie visuels IA', err);
      toast.error('Impossible de charger la galerie');
    } finally {
      setLoadingImages(false);
    }
  }, [adminKey]);

  useEffect(() => { loadImages(); }, [loadImages]);

  const onGenerated = (image) => { setNewImageId(image.id); loadImages(); };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce visuel ? S\'il illustre un conseil, celui-ci n\'aura plus d\'image.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/images/${id}?key=${adminKey}`);
      setImages(prev => prev.filter(i => i.id !== id));
      if (newImageId === id) setNewImageId(null);
      toast.success('Visuel supprimé');
      setMediaRefreshToken(t => t + 1);
    } catch (err) {
      logError('suppression visuel', err);
      toast.error('Suppression impossible');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-images-tab">
      <AdminAiUsageCard adminKey={adminKey} refreshToken={mediaRefreshToken} />
      <AdminAiKeysCard adminKey={adminKey} onKeysChanged={() => setKeysRefreshToken(t => t + 1)} />
      <AdminAiSettingsCard adminKey={adminKey} refreshToken={keysRefreshToken} />
      <AdminCityContentCard adminKey={adminKey} />
      <ImageGeneratorCard adminKey={adminKey} onGenerated={onGenerated} />
      <AdminConseilsMediaCard adminKey={adminKey} refreshToken={mediaRefreshToken} onMediaChanged={loadImages} />
      <ImageGalleryCard images={images} loading={loadingImages} newImageId={newImageId} onRefresh={loadImages} onRemove={remove} />
    </div>
  );
}
